import React from 'react';
import {
  Activity, AlarmClock, Bell, Bug, Calendar, ChartLine, Clock, Code2, Coins, Database, Eye,
  FileText, FlaskConical, GitMerge, GitPullRequest, Hand, Headset, KeyRound, Mail, MessageSquare,
  MessagesSquare, Package, Rocket, ScanSearch, Send, ShieldCheck, Siren, Smile, Sparkles, Ticket,
  TrendingUp, Users, Wallet, Webhook, Wrench,
} from 'lucide-react';
import { APP_LOGOS, LOGO_NEEDS_ADAPTIVE } from 'features/agents/WorkflowEditor/appLogos';

/**
 * Template icon key -> what to draw. The keys are the ones the server's
 * templates (and their categories) name; they are listed in
 * backend/services/workflows/templates/iconKeys.json, and a test holds the
 * two in step. An app's own template wears its real logo, Lucid its mark,
 * everything else a lucide icon.
 *
 * A descriptor is `{ Icon }` or `{ logo, adaptive }`. `adaptive` marks a dark
 * logo that needs inverting on a dark theme (the `node-icon-adaptive` class).
 */
const LUCID_LOGO = '/ask/ask-ember.svg';

const logo = (src) => ({ logo: src, adaptive: LOGO_NEEDS_ADAPTIVE.has(src) });

export const TEMPLATE_ICONS = {
  activity: { Icon: Activity },
  alarm: { Icon: AlarmClock },
  bell: { Icon: Bell },
  bug: { Icon: Bug },
  calendar: { Icon: Calendar },
  chart: { Icon: ChartLine },
  clock: { Icon: Clock },
  code: { Icon: Code2 },
  coins: { Icon: Coins },
  database: { Icon: Database },
  eye: { Icon: Eye },
  'file-text': { Icon: FileText },
  flask: { Icon: FlaskConical },
  'git-merge': { Icon: GitMerge },
  'git-pull-request': { Icon: GitPullRequest },
  github: logo(APP_LOGOS.github),
  hand: { Icon: Hand },
  headset: { Icon: Headset },
  key: { Icon: KeyRound },
  linear: logo(APP_LOGOS.linear),
  lucid: logo(LUCID_LOGO),
  mail: { Icon: Mail },
  message: { Icon: MessageSquare },
  messages: { Icon: MessagesSquare },
  package: { Icon: Package },
  rocket: { Icon: Rocket },
  scan: { Icon: ScanSearch },
  send: { Icon: Send },
  shield: { Icon: ShieldCheck },
  siren: { Icon: Siren },
  slack: logo(APP_LOGOS.slack),
  smile: { Icon: Smile },
  sparkles: { Icon: Sparkles },
  ticket: { Icon: Ticket },
  'trending-up': { Icon: TrendingUp },
  users: { Icon: Users },
  wallet: { Icon: Wallet },
  webhook: { Icon: Webhook },
  wrench: { Icon: Wrench },
};

/** The descriptor for a key; an unknown key falls back to Sparkles rather than drawing nothing. */
export function templateIconFor(key) {
  return TEMPLATE_ICONS[key] || { Icon: Sparkles };
}

/**
 * The icon itself, no tile around it. The caller supplies the tile (a
 * neutral 28px chip) so the chooser, the cards and the editor stay aligned.
 */
export function TemplateIcon({ name, size = 14, className }) {
  const d = templateIconFor(name);
  if (d.logo) {
    return (
      <img
        src={d.logo}
        alt=""
        aria-hidden="true"
        width={size}
        height={size}
        className={[d.adaptive ? 'node-icon-adaptive' : '', className || ''].filter(Boolean).join(' ') || undefined}
        style={{ width: size, height: size, objectFit: 'contain' }}
      />
    );
  }
  const { Icon } = d;
  return <Icon aria-hidden="true" className={className} style={{ width: size, height: size }} />;
}
